/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import React from "react";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import { Stack } from "@mui/system";
import { Box, IconButton } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useSearchId } from "hooks/search";
import { PlanSummary } from "./summary";
import { usePlan } from "contexts/plan";
import { useDefaultPlanName } from "./name";
import { useTabName } from "../common/TabTitle";
import { TitledTabs } from "../common/titledTabs";
import { ProductionTab } from "./productionTab";
import { InventoryTab } from "./inventoryTab";
import { SpreadsheetTab } from "./spreadsheetTab";
import { LoadedContent } from "../common/loading/loadedContent";
import { LoadingResults } from "../search/output/LoadingResults";
import { useNavigateToHome, useNavigateToSearch } from "hooks/navigation";
import { usePlanPageTabHotKeys, usePlanPeekHotKeys } from "hooks/hotkeys";
import { useDeployProcess } from "hooks/useDeployProcess";
import { useIsMobile } from "hooks/useIsMobile";
import { mapLoadedUnpack } from "models/loaded";
import { useSearch } from "contexts/search/SearchContextProvider";
import { usePlanId } from "../common/boundary/PlanId";
import { useSearchName } from "hooks/search";
import { useTenant } from "hooks/settings";
import { generatedApi } from "src/store/api/generatedApi";
import { useAppDispatch } from "src/store/store";
import { filterPlanResultsToPeriod } from "components/common/plan/utils";

export const PlanPage = () => <LoadedPlanPage planId={usePlanId()} />;

export const LoadedPlanPage = ({ planId }: { planId: number }) => {
  usePlanTabName(planId);
  const { t } = useTenantTranslation();
  const {
    results: { results },
  } = useSearch();
  const navigateToSearch = useNavigateToSearch();
  const navigateToHome = useNavigateToHome();
  const searchId = useSearchId();
  const [selectedTab, setSelectedTab] = usePlanPageTabHotKeys();
  usePlanPeekHotKeys();
  const isMobile = useIsMobile();
  const deployProcess = useDeployProcess();
  const showSpreadsheetTab =
    deployProcess === "edit_and_send" || deployProcess === "edit_sync_and_sms";

  const tenantName = useTenant();
  const dispatch = useAppDispatch();
  const { plan: loadedPlan } = usePlan(planId);
  const [getPlanResultsBatch] = generatedApi.useGetPlanResultsBatchMutation();
  const prefetchedRef = React.useRef(false);

  React.useEffect(() => {
    if (prefetchedRef.current) return;
    if (!showSpreadsheetTab) return;
    if (loadedPlan.status !== "success" || !loadedPlan.data) return;

    const periodSummary = loadedPlan.data.summary?.period_summary;
    if (!periodSummary) return;

    const requests = periodSummary.map((period) => ({
      plan_id: planId,
      period: period.period,
    }));

    if (requests.length > 0) {
      prefetchedRef.current = true;
      void getPlanResultsBatch({ tenantName, requests })
        .unwrap()
        .then((response) => {
          response.results.forEach((item) => {
            if (item.response) {
              void dispatch(
                generatedApi.util.upsertQueryData(
                  "getPlanResults",
                  { tenantName, planId: item.plan_id, period: item.period },
                  filterPlanResultsToPeriod(item.response, item.period)
                )
              );
            }
          });
        });
    }
  }, [
    loadedPlan,
    planId,
    tenantName,
    getPlanResultsBatch,
    dispatch,
    showSpreadsheetTab,
  ]);

  const [showExtendedSummary, setShowExtendedSummary] = React.useState(true);

  const toggleShowExtendedSummary = () => {
    setShowExtendedSummary((showExtendedSummary) => !showExtendedSummary);
  };
  return (
    <LoadedContent
      data={results}
      loading={<LoadingResults includeText={false} />}
      waiting={<LoadingResults includeText={false} />}
      disableFetching
    >
      {(result) => {
        if (!result?.plans.some((plan) => plan.id === planId)) {
          if (searchId !== null) {
            navigateToSearch(searchId);
          } else {
            navigateToHome();
          }
          return null;
        }
        return (
          <Stack sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <Stack gap={2} sx={{ px: 3, pt: 2, pb: 1, flexShrink: 0 }}>
              <PlanSummary
                planId={planId}
                showExtendedSummary={showExtendedSummary}
              />
            </Stack>

            <Box
              sx={{
                px: 3,
                pb: 2,
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "auto",
              }}
            >
              {isMobile ? (
                <ProductionTab planId={planId} />
              ) : (
                <TitledTabs
                  selectedTabKey={selectedTab}
                  onChange={(key) => {
                    setSelectedTab(key);
                  }}
                  tabSpecs={[
                    {
                      title: t("production"),
                      key: "production",
                      content: <ProductionTab planId={planId} />,
                    },
                    ...(showSpreadsheetTab
                      ? [
                          {
                            title: t("basketBreakdown"),
                            key: "spreadsheet" as const,
                            content: <SpreadsheetTab planId={planId} />,
                          },
                        ]
                      : []),
                    {
                      title: t("inventory"),
                      key: "inventory",
                      content: <InventoryTab planId={planId} />,
                    },
                  ]}
                  rightComponent={
                    showExtendedSummary ? (
                      <IconButton onClick={toggleShowExtendedSummary}>
                        <KeyboardDoubleArrowUpIcon />
                      </IconButton>
                    ) : (
                      <IconButton onClick={toggleShowExtendedSummary}>
                        <KeyboardDoubleArrowDownIcon />
                      </IconButton>
                    )
                  }
                />
              )}
            </Box>
          </Stack>
        );
      }}
    </LoadedContent>
  );
};

const usePlanTabName = (planId: number) => {
  const { plan: loadedPlan } = usePlan(planId);
  const defaultPlanName = useDefaultPlanName()(
    mapLoadedUnpack(loadedPlan, (plan) => plan.plan_index)
  );
  const searchName = useSearchName();

  const name = mapLoadedUnpack(searchName, (name) => {
    return defaultPlanName == null ? name : `${defaultPlanName} - ${name}`;
  });

  useTabName(name);
};
